import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Fieldset, Heading, Paragraph } from '@digdir/designsystemet-react';
import { ChevronLeftIcon, ChevronRightIcon } from '@navikt/aksel-icons';
import { useNavigate } from 'react-router-dom';

import useRegistrationForm from '../../../registration/useRegistrationForm';
import { EnergimerkeSummary } from '../../../expertRegistration/json/common/summary/energimerkeSummary';
import { useEnergyAnalysis } from '../../../../hooks/useEnergyAnalysis';
import { fromEnergiplanToEnergimerke } from '../../../../components/energimerke/utils';
import { RegistrationCommonStep } from '../../../../utils/navigation';
import { useRegistrationSteps } from '../../hooks/useRegistrationSteps';
import { usePublishRegistration } from '../../../../hooks/usePublishRegistration';
import { ApiRequestError, toApiRequestError } from '../../../../services/api';
import { PageWrapper } from '../solceller';
import { FormContentWrapper } from '../omBoligen/form';
import { ButtonWrapper } from '../solceller/form/components/styledComponents';
import { ErrorBox } from '../../../../components/errorBox';

import { SummaryReview } from './review';
import { MalformOgSynlighetViewer } from './malformOgSynlighetViewer';
import { Preview } from './preview';

const Oppsummering: FC = () => {
  const { t: tSummary } = useTranslation('', { keyPrefix: 'expertToolsJSON.summary' });
  const { registrationId, tiltakList } = useRegistrationForm();
  const navigate = useNavigate();
  const [publishError, setPublishError] = useState<ApiRequestError | null>(null);
  const { energyAnalysis } = useEnergyAnalysis(registrationId);

  const energiplan = energyAnalysis?.energiplan;
  const energimerke = fromEnergiplanToEnergimerke(energyAnalysis?.energiplan);
  const isPublished = energyAnalysis?.registrering.publisertDato;
  const { previousStep } = useRegistrationSteps(RegistrationCommonStep.SUMMARY);
  const { publishRegistration, publishIsLoading } = usePublishRegistration(
    registrationId,
    energyAnalysis?.registrering.energiObjektId
  );
  if (!energimerke || !energiplan) {
    return null;
  }

  if (!registrationId) {
    return null;
  }

  const onConfirm = () => {
    setPublishError(null);
    publishRegistration(undefined, {
      onSuccess: () => navigate(`../energiattest`),
      onError: (error) => {
        setPublishError(toApiRequestError(error, 'Could not publish registration'));
      }
    });
  };

  return (
    <PageWrapper>
      <Fieldset disabled={publishIsLoading}>
        <Heading level={1} data-size="lg">
          {tSummary('title')}
        </Heading>
        <FormContentWrapper>
          <Paragraph data-size="lg">{tSummary('description')}</Paragraph>
          {energimerke && <EnergimerkeSummary energimerke={energimerke} published={isPublished} />}
          <MalformOgSynlighetViewer energyAnalysis={energyAnalysis} />
          <SummaryReview registrationId={registrationId} energiplan={energiplan} tiltakList={tiltakList} />
          <Preview registrationId={registrationId} />
          <ErrorBox error={publishError} registrationId={registrationId} />
          <ButtonWrapper>
            <Button variant="secondary" disabled={publishIsLoading} onClick={() => navigate(`../${previousStep}`)}>
              <ChevronLeftIcon />
              {tSummary('navigation.forrige')}
            </Button>
            <Button onClick={onConfirm} loading={publishIsLoading}>
              {tSummary('navigation.bekreft')} <ChevronRightIcon />
            </Button>
          </ButtonWrapper>
        </FormContentWrapper>
      </Fieldset>
    </PageWrapper>
  );
};

export default Oppsummering;
